/**
 * SymbolTable — a lookup index of every named entity in the workspace
 * (modules, surfaces, interfaces, processes, views, types) plus the source
 * location where each was defined.
 *
 * Built from the workspace's resolved model. For navigation features
 * (hover, definition, find-references, workspace-symbols, rename).
 */

import type {
  ProcessDecl,
  QualifiedName,
  SourceSpan,
  StableId,
  SubprocessDecl,
  TypeDecl,
  ViewDeclV2,
} from "@archlang/engine";
import { aspectKey, joinDescriptions, synthesizeStubs } from "@archlang/engine";
import type {
  ResolvedSurface,
  ResolvedInterface,
  ResolvedModule,
} from "@archlang/engine";
import type { Workspace } from "./workspace.js";
import { forEachParsedAspectValue } from "./aspect-refs.js";
import { forEachReentryOccurrence, reentryLabelId } from "./reentry-labels.js";

export type SymbolKind =
  | "module"
  | "surface"
  | "interface"
  | "process"
  | "subprocess"
  | "view"
  | "type"
  | "aspect"
  /** A process-local `as` / `go` re-entry label (PROCESS.md §7). */
  | "reentry";

export interface SymbolEntry {
  /** Stable ID where applicable. Synthesized for kinds without IDs. */
  readonly id: string;
  readonly kind: SymbolKind;
  /** Simple name as it appears in source ("CreateOrder", "AuthService"). */
  readonly name: string;
  /** Owner's qualified name for nested entities; undefined for top-level. */
  readonly ownerName?: string;
  /** Fully qualified lookup key (`Owner.Name` for nested). */
  readonly qualifiedName: string;
  readonly definingUri?: string;
  /** Span of the name identifier specifically (preferred for highlights). */
  readonly nameSpan?: SourceSpan;
  /** Span of the entire declaration. */
  readonly declSpan?: SourceSpan;
  readonly description?: string;
  /** The kind keyword used in source (`microservice`, `command`, …). */
  readonly typeName?: string;
  /** For type symbols, the resolved base kind (`module`, `interface`, …). */
  readonly typeBase?: string;
  /** A draft stub (§4.13): referenced in a process but never declared. The
   *  compiler synthesizes it as a first-class module/interface marked TODO; it
   *  has no declaration site, so `definingUri`/`nameSpan` are absent. */
  readonly synthesized?: boolean;
}

/** Id prefix for a synthesized stub symbol (no declaration). */
export const STUB_SYMBOL_PREFIX = "stub:";

export class SymbolTable {
  private readonly byName = new Map<string, SymbolEntry>();
  /** EVERY entry per qualified name (v0.11): cross-space twins legally share
   *  a bare path — `byName` keeps one winner, this keeps them all so
   *  referrer-space resolution can pick the right one. */
  private readonly byNameAll = new Map<string, SymbolEntry[]>();
  /** Per-file landed space (dotted) — the referrer side of space-aware
   *  resolution. */
  private readonly spaceOfUri: ReadonlyMap<string, QualifiedName>;
  private readonly byOwner = new Map<string, SymbolEntry[]>();
  private readonly entries: SymbolEntry[] = [];
  /** Canonical aspect entry per `(keyPath, value)` identity. */
  private readonly byAspect = new Map<string, SymbolEntry>();
  /** Every aspect *occurrence* span, for cursor resolution at any use-site. */
  private readonly aspectOccurrences: { entry: SymbolEntry; uri: string; span: SourceSpan }[] = [];
  /** Canonical re-entry-label entry per `reentry:<procKey>:<label>` id. */
  private readonly byReentry = new Map<string, SymbolEntry>();
  /** Every re-entry-label `as`/`go` span, for cursor resolution at any site. */
  private readonly reentryOccurrences: { entry: SymbolEntry; uri: string; span: SourceSpan }[] = [];

  constructor(workspace: Workspace) {
    const model = workspace.model();
    this.spaceOfUri = workspace.fileSpaces();

    for (const mod of model.modules) this.indexModule(mod, undefined);
    for (const proc of model.processes) this.add(processEntry(proc));
    for (const sp of model.subprocesses) this.add(subprocessEntry(sp));
    for (const view of model.views) this.add(viewEntry(view));
    for (const type of model.types) this.add(typeEntry(type, model));
    this.indexAspects(workspace);
    this.indexReentryLabels(workspace);

    // Promote synthesized stubs (§4.13) to first-class symbols: a name used in
    // a process step but never declared is a draft the compiler stubs as a real
    // module/interface (marked TODO). Indexing them here means rename, find-
    // references, and hover treat drafts as proper elements — not second-class.
    // A real declaration always wins (addStub is a no-op if the name exists).
    const stubs = synthesizeStubs(model);
    for (const p of stubs.modulePaths) this.addStub(p, "module");
    for (const p of stubs.interfacePaths) this.addStub(p, "interface");
  }

  /** Add a synthesized-stub symbol for an undeclared path, unless a real
   *  declaration already owns that name. */
  private addStub(path: string, kind: "module" | "interface"): void {
    if (this.byName.has(path)) return;
    const segs = path.split(".");
    const entry: SymbolEntry = {
      id: STUB_SYMBOL_PREFIX + path,
      kind,
      name: segs[segs.length - 1]!,
      ownerName: segs.length > 1 ? segs.slice(0, -1).join(".") : undefined,
      qualifiedName: path,
      synthesized: true,
    };
    this.byName.set(path, entry);
    this.entries.push(entry);
    const owner = entry.ownerName ?? "";
    const list = this.byOwner.get(owner) ?? [];
    list.push(entry);
    this.byOwner.set(owner, list);
  }

  /** v0.8.2 hoisting: written short/suffix refs → the unique full symbol.
   *  Built lazily; `null` marks an AMBIGUOUS suffix (stays unresolved). */
  private suffixIndex?: Map<string, SymbolEntry | null>;

  private buildSuffixIndex(): Map<string, SymbolEntry | null> {
    const count = new Map<string, number>();
    const one = new Map<string, SymbolEntry>();
    for (const [full, entry] of this.byName) {
      // Module names hoist by any suffix; an interface hoists by its owner-suffix
      // + leaf (never the bare leaf alone — interfaces have no stable id).
      if (entry.kind !== "module" && entry.kind !== "interface") continue;
      const segs = full.split(".");
      const minK = entry.kind === "interface" ? 2 : 1;
      for (let k = minK; k < segs.length; k++) {
        const suf = segs.slice(segs.length - k).join(".");
        count.set(suf, (count.get(suf) ?? 0) + 1);
        one.set(suf, entry);
      }
    }
    const out = new Map<string, SymbolEntry | null>();
    for (const [suf, entry] of one) {
      const existing = this.byName.get(suf);
      // A REAL (declared) exact key wins; a synthesized STUB does not — a hoisted
      // real symbol should resolve THROUGH a phantom stub of the written short ref.
      if (existing && !existing.synthesized) continue;
      out.set(suf, count.get(suf) === 1 ? entry : null);
    }
    return out;
  }

  resolve(qualifiedName: string, fromUri?: string): SymbolEntry | undefined {
    // v0.11: cross-space twins legally share a qualified path — resolve a
    // bare ref from the REFERRER's space chain, nearest-first, root last
    // (mirrors the engine's `resolveScoped`). Only kicks in with >1 real
    // candidate; unique names take the plain exact path below.
    if (fromUri) {
      const cands = (this.byNameAll.get(qualifiedName) ?? []).filter((e) => !e.synthesized);
      if (cands.length > 1) {
        const spaceKeyOf = (uri: string | undefined): string =>
          uri ? (this.spaceOfUri.get(uri) ?? []).join(".") : "";
        const ref = this.spaceOfUri.get(fromUri) ?? [];
        for (let k = ref.length; k >= 0; k--) {
          const want = ref.slice(0, k).join(".");
          const hit = cands.find((e) => spaceKeyOf(e.definingUri) === want);
          if (hit) return hit;
        }
      }
    }
    const exact = this.byName.get(qualifiedName);
    if (exact && !exact.synthesized) return exact; // a real exact match always wins
    this.suffixIndex ??= this.buildSuffixIndex();
    if (this.suffixIndex.has(qualifiedName)) {
      // A real hoist beats a synthesized stub; `null` = ambiguous → unresolved.
      return this.suffixIndex.get(qualifiedName) ?? undefined;
    }
    return exact; // fall back to a synthesized stub, if that's all there is
  }

  /** Resolve an aspect identity to its canonical symbol entry. */
  resolveAspect(keyPath: readonly string[], value: string): SymbolEntry | undefined {
    return this.byAspect.get(aspectKey(keyPath, value));
  }

  /**
   * An aspect (string label value) has many use-sites and no single
   * declaration. Find the aspect whose value-token span contains the
   * cursor offset — lets hover / find-references / rename trigger from
   * any occurrence.
   */
  aspectAt(uri: string, offset: number): SymbolEntry | undefined {
    for (const occ of this.aspectOccurrences) {
      if (occ.uri !== uri) continue;
      if (occ.span.start.offset <= offset && offset <= occ.span.end.offset) {
        return occ.entry;
      }
    }
    return undefined;
  }

  private indexAspects(workspace: Workspace): void {
    forEachParsedAspectValue(workspace, (occ) => {
      if (occ.cls !== "aspect" || !occ.valueSpan) return;
      const value = occ.value.kind === "string" ? occ.value.text : "";
      const ak = aspectKey(occ.keyPath, value);
      let entry = this.byAspect.get(ak);
      if (!entry) {
        entry = {
          id: `aspect:${ak}`,
          kind: "aspect",
          name: value,
          qualifiedName: ak,
          definingUri: occ.uri,
          nameSpan: occ.valueSpan,
          declSpan: occ.valueSpan,
        };
        this.byAspect.set(ak, entry);
        this.entries.push(entry);
      }
      this.aspectOccurrences.push({ entry, uri: occ.uri, span: occ.valueSpan });
    });
  }

  /**
   * A re-entry label (`as` / `go`, PROCESS.md §7) is process-local and has no
   * workspace symbol id — so, like an aspect, synthesize one keyed by the
   * owning process plus the name. The DEFINITION is the first `as` occurrence;
   * subsequent `as` (a duplicate authoring) and every `go` are use-sites that
   * resolve to the same entry. Find-references / rename ride the generic path
   * via the entry's id; the `as`-def is the declaration (nameSpan), the `go`s
   * are recorded references in the ReferenceIndex.
   */
  private indexReentryLabels(workspace: Workspace): void {
    forEachReentryOccurrence(workspace, (occ) => {
      const id = reentryLabelId(occ.procKey, occ.label);
      let entry = this.byReentry.get(id);
      if (!entry) {
        // The first occurrence we see seeds the entry. We only adopt an `as`
        // occurrence as the declaration (nameSpan) — a `go`-first ordering
        // leaves nameSpan unset until the `as` is seen (handled below).
        entry = {
          id,
          kind: "reentry",
          name: occ.label,
          qualifiedName: id,
          definingUri: occ.role === "as" ? occ.uri : undefined,
          nameSpan: occ.role === "as" ? occ.span : undefined,
          declSpan: occ.role === "as" ? occ.span : undefined,
        };
        this.byReentry.set(id, entry);
        this.entries.push(entry);
      } else if (occ.role === "as" && !entry.nameSpan) {
        // A `go` was seen before the `as` declaration — promote the def now.
        const promoted: SymbolEntry = {
          ...entry,
          definingUri: occ.uri,
          nameSpan: occ.span,
          declSpan: occ.span,
        };
        this.byReentry.set(id, promoted);
        const idx = this.entries.indexOf(entry);
        if (idx >= 0) this.entries[idx] = promoted;
        // Re-point earlier occurrence records at the promoted entry.
        for (const r of this.reentryOccurrences) {
          if (r.entry.id === id) (r as { entry: SymbolEntry }).entry = promoted;
        }
        entry = promoted;
      }
      this.reentryOccurrences.push({ entry, uri: occ.uri, span: occ.span });
    });
  }

  /**
   * A re-entry label has many use-sites (`as` def + every `go`). Find the
   * label whose `as`-def OR any `go` span contains the cursor offset — lets
   * hover / find-references / rename trigger from any occurrence.
   */
  reentryLabelAt(uri: string, offset: number): SymbolEntry | undefined {
    for (const occ of this.reentryOccurrences) {
      if (occ.uri !== uri) continue;
      if (occ.span.start.offset <= offset && offset <= occ.span.end.offset) {
        return occ.entry;
      }
    }
    return undefined;
  }

  /**
   * Find the smallest-span symbol whose declaration contains the given
   * offset in the given file. Used for "what is at this cursor?"
   */
  resolveAt(uri: string, offset: number): SymbolEntry | undefined {
    let best: SymbolEntry | undefined;
    let bestSize = Infinity;
    for (const sym of this.entries) {
      if (sym.definingUri !== uri) continue;
      const span = sym.declSpan ?? sym.nameSpan;
      if (!span) continue;
      if (span.start.offset <= offset && offset <= span.end.offset) {
        const size = span.end.offset - span.start.offset;
        if (size < bestSize) {
          best = sym;
          bestSize = size;
        }
      }
    }
    return best;
  }

  all(): readonly SymbolEntry[] {
    return this.entries;
  }

  /** Every NON-synthesized entry sharing this qualified name — cross-space
   *  twins legally share one bare path (v0.13); collision checks compare
   *  landed spaces instead of assuming one winner. */
  declaredWithName(qualifiedName: string): readonly SymbolEntry[] {
    return (this.byNameAll.get(qualifiedName) ?? []).filter((e) => !e.synthesized);
  }

  /** Dotted landed-space key of a file ("" = package root / unknown). */
  spaceKeyOfUri(uri: string | undefined): string {
    return uri ? (this.spaceOfUri.get(uri) ?? []).join(".") : "";
  }

  /** Only DECLARED symbols (excludes synthesized stubs). Completion uses this
   *  for its declared lists; stubs reach completion via the ghost path so they
   *  rank as drafts, not as authored elements. */
  declared(): readonly SymbolEntry[] {
    return this.entries.filter((e) => !e.synthesized);
  }

  childrenOf(qualifiedName: string): readonly SymbolEntry[] {
    return this.byOwner.get(qualifiedName) ?? [];
  }

  interfacesOf(moduleQualifiedName: string): readonly SymbolEntry[] {
    return (this.byOwner.get(moduleQualifiedName) ?? []).filter(
      (s) => s.kind === "interface"
    );
  }

  private indexModule(mod: ResolvedModule, parentName: string | undefined): void {
    const qname = parentName ? `${parentName}.${mod.name}` : mod.name;
    this.add({
      // Synthesized ids carry the LANDED space (v0.13): cross-space twins
      // legally share the bare qualified name, and a shared id merges their
      // reference buckets — find-refs/rename would leak into the other twin.
      id: mod.id ?? `module:${spaceTag(mod.space)}${qname}`,
      kind: "module",
      name: mod.name,
      ownerName: parentName,
      qualifiedName: qname,
      definingUri: mod.decl.source?.file,
      nameSpan: mod.decl.nameSource,
      declSpan: mod.decl.source,
      description: joinDescriptions(mod.body.descriptions),
      typeName: mod.typeName,
    });

    for (const iface of mod.interfaces) {
      this.add(interfaceEntry(iface, qname, mod.space));
    }

    for (const surface of mod.surfaces) {
      this.indexSurface(surface, qname, mod.space);
    }

    for (const child of mod.modules) {
      this.indexModule(child, qname);
    }
  }

  private indexSurface(
    surface: ResolvedSurface,
    parentName: string,
    ownerSpace: QualifiedName | undefined,
  ): void {
    const qname = `${parentName}.${surface.name}`;
    this.add({
      id: `surface:${spaceTag(ownerSpace)}${qname}`,
      kind: "surface",
      name: surface.name,
      ownerName: parentName,
      qualifiedName: qname,
      definingUri: surface.decl.source?.file,
      nameSpan: surface.decl.nameSource,
      declSpan: surface.decl.source,
      description: joinDescriptions(surface.body.descriptions),
      typeName: surface.typeName,
    });

    for (const iface of surface.interfaces) {
      this.add(interfaceEntry(iface, qname, ownerSpace));
    }
    for (const sub of surface.surfaces) {
      this.indexSurface(sub, qname, ownerSpace);
    }
  }

  private add(entry: SymbolEntry): void {
    this.entries.push(entry);
    this.byName.set(entry.qualifiedName, entry);
    const all = this.byNameAll.get(entry.qualifiedName) ?? [];
    all.push(entry);
    this.byNameAll.set(entry.qualifiedName, all);
    if (entry.ownerName) {
      const list = this.byOwner.get(entry.ownerName) ?? [];
      list.push(entry);
      this.byOwner.set(entry.ownerName, list);
    }
  }
}

/** `Space.Path::` id prefix for a landed space; empty when spaceless — the
 *  v0.13 twin-distinguishing term of every synthesized symbol id. */
function spaceTag(space: QualifiedName | undefined): string {
  return space && space.length > 0 ? `${space.join(".")}::` : "";
}

function interfaceEntry(
  iface: ResolvedInterface,
  ownerName: string,
  ownerSpace: QualifiedName | undefined,
): SymbolEntry {
  return {
    id: `iface:${spaceTag(ownerSpace)}${ownerName}.${iface.name}`,
    kind: "interface",
    name: iface.name,
    ownerName,
    qualifiedName: `${ownerName}.${iface.name}`,
    definingUri: iface.decl.source?.file,
    nameSpan: iface.decl.nameSource,
    declSpan: iface.decl.source,
    description: joinDescriptions(iface.body.descriptions),
    typeName: iface.typeName,
  };
}

function processEntry(proc: ProcessDecl): SymbolEntry {
  const owner = proc.ownerPath && proc.ownerPath.length > 0
    ? proc.ownerPath.join(".")
    : undefined;
  const qname = owner ? `${owner}.${proc.name}` : proc.name;
  return {
    id: proc.id ?? `proc:${spaceTag(proc.space)}${qname}`,
    kind: "process",
    name: proc.name,
    ownerName: owner,
    qualifiedName: qname,
    definingUri: proc.source?.file,
    nameSpan: proc.nameSource,
    declSpan: proc.source,
    description: proc.description,
  };
}

function subprocessEntry(sp: SubprocessDecl): SymbolEntry {
  const owner = sp.ownerPath && sp.ownerPath.length > 0
    ? sp.ownerPath.join(".")
    : undefined;
  const qname = owner ? `${owner}.${sp.name}` : sp.name;
  return {
    id: sp.id ?? `subproc:${spaceTag(sp.space)}${qname}`,
    kind: "subprocess",
    name: sp.name,
    ownerName: owner,
    qualifiedName: qname,
    definingUri: sp.source?.file,
    nameSpan: sp.nameSource,
    declSpan: sp.source,
    description: sp.description,
  };
}

function viewEntry(view: ViewDeclV2): SymbolEntry {
  return {
    id: view.id ?? `view:${spaceTag(view.space)}${view.name}`,
    kind: "view",
    name: view.name,
    qualifiedName: view.name,
    definingUri: view.source?.file,
    nameSpan: view.nameSource,
    declSpan: view.source,
    description: view.description,
  };
}

function typeEntry(
  type: TypeDecl,
  model: {
    registry: { kinds: ReadonlyMap<string, { baseType: string }> };
    spaces?: ReadonlyMap<string, QualifiedName>;
  }
): SymbolEntry {
  const baseType = model.registry.kinds.get(type.name)?.baseType ?? type.parentType;
  // Types are space-scoped (registry `space::name`); the decl's space comes
  // from its declaring file — same twin-id law as the other families.
  const space = type.source?.file ? model.spaces?.get(type.source.file) : undefined;
  return {
    id: type.id ?? `type:${spaceTag(space)}${type.name}`,
    kind: "type",
    name: type.name,
    qualifiedName: type.name,
    definingUri: type.source?.file,
    nameSpan: type.nameSource,
    declSpan: type.source,
    typeBase: baseType,
    // The type's own docstring — surfaced on hover at use-sites (a `postgres`
    // token in `postgres Store {}` shows what the kind is), suppressed at the
    // declaration where the body is already on screen.
    description: joinDescriptions(type.body.descriptions),
  };
}
